import {
  BaseModel,
  MixedSourceWatchable,
  ReadonlyWatchable,
  Watchable,
  type ReadonlyEventWatchable,
} from '@cotera/client/v0/actions/framework';
import {
  ChatAction,
  MessageClass,
  type ChatActionData,
} from '@cotera/sdk/core';
import type { ChatStatusFrameOutput } from '@cotera/api';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { UIMessage } from 'ai';
import { getStackFrameStatusInfo } from '../chat/utils/coco-stack-frame-status';
import { type ChatStatus } from '../chat/models/coco-chat.model';
import { WatchablesFactory } from '../watchables/watchables-factory';
import { shouldTreatNoStackFramesAsCompleted } from '../components/sub-agent-tool.model';

type ConversationSnapshotAction = {
  id: string;
  chatId: string;
  type: string;
  status: string;
  data: Record<string, unknown> | null;
  completedAt: Date | string | null;
  approvedAt: Date | string | null;
  approvedBy: string | null;
  createdAt: Date | string;
  updatedAt: Date | string;
};

type ConversationSnapshot = {
  stackFrames: ChatStatusFrameOutput[];
  messages: Array<{ id: string; message?: unknown; createdAt?: Date | string }>;
  actions: ConversationSnapshotAction[];
};

const EMPTY_SNAPSHOT: ConversationSnapshot = {
  stackFrames: [],
  messages: [],
  actions: [],
};

const toDate = (value: Date | string | null): Date | null => {
  if (value === null) {
    return null;
  }
  return value instanceof Date ? value : new Date(value);
};

const convertActionsToChatActions = (
  actions: ConversationSnapshotAction[]
): ChatAction[] =>
  actions.map((action) =>
    ChatAction.fromData({
      id: action.id,
      actorId: null,
      chatId: action.chatId,
      type: action.type as ChatActionData['type'],
      status: action.status as ChatActionData['status'],
      data: action.data,
      completedAt: toDate(action.completedAt),
      approvedAt: toDate(action.approvedAt),
      approvedBy: action.approvedBy,
      createdAt: toDate(action.createdAt)!,
      updatedAt: toDate(action.updatedAt)!,
    })
  );

const convertListMessagesToMessageClass = (
  chatId: string,
  messages: Array<{ id: string; message?: unknown; createdAt?: Date | string }>,
  actions: ChatAction[]
): MessageClass[] =>
  messages
    .filter(
      (
        message
      ): message is {
        id: string;
        message: unknown;
        createdAt?: Date | string;
      } => message.message !== undefined
    )
    .map((message) => {
      const uiMessage = message.message as UIMessage;
      const type =
        uiMessage.id?.startsWith('compaction') === true ? 'compaction' : 'chat';
      const createdAt = toDate(message.createdAt ?? null);
      return MessageClass.fromUIMessage(
        chatId,
        uiMessage,
        type,
        actions,
        createdAt !== null ? { createdAt } : undefined
      );
    });

export type WorkflowRunAgentConversationModelProps = {
  chatId: string;
  /** True while the workflow step is still queued or running. */
  isActive: boolean;
  client: Pick<TenantedClient['resource']['chat'], 'subAgentSnapshot'>;
  watchablesFactory: Pick<WatchablesFactory, 'makeSubAgentSnapshotWatchable'>;
};

/** Loads a workflow agent conversation for readonly display with live updates. */
export class WorkflowRunAgentConversationModel extends BaseModel {
  readonly messagesWatchable: ReadonlyWatchable<MessageClass[]>;
  readonly statusWatchable: MixedSourceWatchable<ChatStatus>;
  readonly snapshotWatchable: ReadonlyEventWatchable<ConversationSnapshot>;

  constructor(props: WorkflowRunAgentConversationModelProps) {
    super(`workflow-run-agent-conversation-${props.chatId}`);

    const snapshotEventWatchable =
      props.watchablesFactory.makeSubAgentSnapshotWatchable<
        ConversationSnapshot,
        {
          chatId?: string;
          conversationId?: string;
          conversationAncestry?: string[];
          resourceType?: string;
          resourceId?: string;
          messagePosition?: unknown;
        }
      >({
        runOnMount: true,
        initialValue: EMPTY_SNAPSHOT,
        filter: (payload) => {
          if (payload === undefined || payload === null) {
            return false;
          }
          const record = payload as Record<string, unknown>;
          if (record['chatId'] === props.chatId) {
            return true;
          }
          if (record['conversationId'] === props.chatId) {
            return true;
          }
          const ancestry = record['conversationAncestry'] as
            | string[]
            | undefined;
          if (ancestry?.includes(props.chatId) === true) {
            return true;
          }
          if (
            record['resourceType'] === 'chat' &&
            typeof record['resourceId'] === 'string' &&
            record['resourceId'] === props.chatId &&
            (record['conversationAncestry'] !== undefined ||
              record['messagePosition'] !== undefined)
          ) {
            return true;
          }
          return false;
        },
        fn: async () => {
          const result = await props.client.subAgentSnapshot({
            conversationId: props.chatId,
          });
          if (result.isOk()) {
            return {
              stackFrames: result.value.stackFrames,
              messages: result.value.messages,
              actions: (result.value.actions ??
                []) as ConversationSnapshotAction[],
            };
          }
          return EMPTY_SNAPSHOT;
        },
      });

    this.messagesWatchable = Watchable.from((get) => {
      const snapshot = get(snapshotEventWatchable);
      const actions = convertActionsToChatActions(snapshot.actions);
      return convertListMessagesToMessageClass(
        props.chatId,
        snapshot.messages,
        actions
      );
    });
    this.statusWatchable = MixedSourceWatchable.from((get) => {
      const snapshot = get(snapshotEventWatchable);
      if (snapshot.stackFrames.length === 0) {
        if (shouldTreatNoStackFramesAsCompleted(snapshot)) {
          return 'ready';
        }
        return (props.isActive ? 'submitted' : 'ready') as ChatStatus;
      }
      const frameInfo = getStackFrameStatusInfo(snapshot.stackFrames);
      if (
        frameInfo.statusKey === 'cancelled' ||
        (frameInfo.isComplete && frameInfo.statusKey !== 'failed')
      ) {
        return 'ready';
      }
      if (frameInfo.statusKey === 'failed') {
        return 'error';
      }
      return 'streaming';
    });
    this.snapshotWatchable = snapshotEventWatchable;
  }

  dispose(): void {
    this.snapshotWatchable.unsubscribe();
    this.statusWatchable.unsubscribe();
  }
}
